import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { AumicoComponentsModule } from '../../../../../aumico-components/src/lib';
import { Paths } from '../../enums/paths.enum';
import { RouteLabels } from '../../enums/routeLabels.enum';
import { AuthGuard } from '../../shared/auth.guard';
import { AbcpPreviewRedirectGuard } from '../abcp/abcp-preview-redirect.guard';
import { AbcpPage } from '../abcp/abcp.page';
import { CpmmPage } from '../cpmm/cpmm.page';
import { FinancialStatementEditPage } from './financial-statement-edit/financial-statement-edit.page';
import { PersonMandateManagementPage } from './person-mandate-management/person-mandate-management.page';
import { PersonProjectsOverviewPage } from './person-projects-overview/person-projects-overview.page';
import { ProjectsOverviewPage } from './projects-overview/projects-overview.page';
import { RECREATE_ON_PROJECT_CHANGE } from './project-route-reuse.strategy';

const routes: Routes = [
    {
        path: '',
        children: [
            { path: '', redirectTo: `${Paths.OVERVIEW}`, pathMatch: 'full' },
            {
                path: `${Paths.OVERVIEW}`,
                component: ProjectsOverviewPage,
                canActivate: [AuthGuard],
                data: {
                    label: RouteLabels.PROJECT_LIST,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            // REV-22155 — mandate management of a person project
            {
                path: `${Paths.PERSON_PROJECTS}/:projectId/${Paths.MANDATE_MANAGEMENT}`,
                component: PersonMandateManagementPage,
                canActivate: [AuthGuard],
                data: {
                    label: RouteLabels.PROJECT_LIST,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            // REV-22155 — projects page "Personen" (before `:id`, which would match it too)
            {
                path: `${Paths.PERSON_PROJECTS}`,
                component: PersonProjectsOverviewPage,
                canActivate: [AuthGuard],
                data: {
                    label: RouteLabels.PROJECT_LIST,
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                },
            },
            {
                path: `${Paths.REPORT_CPMM}`,
                component: CpmmPage,
                loadChildren: () => import('../../microservices/cpmm/cpmm.module').then((m) => m.CpmmModule),
                // AbcpPreviewRedirectGuard funnels opted-in orgs (abcpPreview flag) to :id/abcp/** (REV-21518, ADR 0002).
                canActivate: [AuthGuard, AbcpPreviewRedirectGuard],
                data: {
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                    // REV-22579: new pages for a new project — see ProjectRouteReuseStrategy.
                    [RECREATE_ON_PROJECT_CHANGE]: true,
                },
            },
            // REV-21518: sibling to REPORT_CPMM. Same guard/data, same content pages, but hosted by
            // AbcpPage (vertical ham-navigation-bar) instead of CpmmPage (horizontal tab nav).
            {
                path: `${Paths.REPORT_ABCP}`,
                component: AbcpPage,
                loadChildren: () => import('../../microservices/abcp/abcp.module').then((m) => m.AbcpModule),
                canActivate: [AuthGuard],
                data: {
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                    // REV-22579: new pages for a new project — see ProjectRouteReuseStrategy.
                    [RECREATE_ON_PROJECT_CHANGE]: true,
                },
            },
            {
                path: `${Paths.PROJECT_ID}`,
                component: FinancialStatementEditPage,
                loadChildren: () =>
                    import(
                        '../../microservices/ms-annual-reports/financial-statement-edit/financial-statement-edit.module'
                    ).then((m) => m.FinancialStatementEditModule),
                canActivate: [AuthGuard],
                data: {
                    canSeeNavigationLinkRoles: ['AnnualReportUser'],
                    // REV-22579: new pages for a new project — see ProjectRouteReuseStrategy.
                    [RECREATE_ON_PROJECT_CHANGE]: true,
                },
            },
        ],
    },
];

@NgModule({
    imports: [CommonModule, AumicoComponentsModule, I18NextModule, RouterModule.forChild(routes)],
})
export class AnnualReportModule {}
